import type { UpdateLocationInput } from 'app/generated/gql/graphql'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { LocationType } from 'app/types/entities'
import { OptionType } from 'types/OptionType'
import { useLocationStore } from 'app/stores/StoreContext'
import { gqlQuery } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'

const LOCATION_SMS_NUMBERS_QUERY = graphql(`
  query LocationSmsNumbers($locationId: ID!) {
    locationSmsNumbers(locationId: $locationId) {
      phone
      provider
      label
    }
  }
`)

type NumberProvider = `Twilio` | `CallRail`

/** Brand marks shipped in public/ — shown by the combo's meta.icon renderer. */
const PROVIDER_ICONS: Record<NumberProvider, string> = {
  Twilio: `/twilio.png`,
  CallRail: `/callrail.png`,
}

type SendableNumberOption = OptionType & {
  meta: { provider: NumberProvider; icon: string; iconAlt: string }
}

/** Shape of a locationSmsNumbers hit (TRK-5419): provider is lowercase. */
type SmsNumberHit = { phone: string; provider: string; label?: string | null }

export type UseLocationPhoneNumbersReturn = {
  location: LocationType | undefined
  trackingNumberOptions: OptionType[]
  selectedTrackingNumber: OptionType | undefined
  setSelectedTrackingNumber: (v: OptionType | undefined) => void
  smsNumberOptions: OptionType[]
  selectedSmsNumber: OptionType | undefined
  setSelectedSmsNumber: (v: OptionType | undefined) => void
  hasCallRailIntegration: boolean
  hasTwilioNumbers: boolean
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  formRef: React.RefObject<HTMLFormElement | null>
  defaultValues: { officeMainNumber: string | undefined; officeTransferNumber: string | undefined } | undefined
  hiddenInputs: { name: string; value: string | undefined }[]
  onSubmit: (data?: Record<string, unknown>) => void
  refetchLocation: () => void
}

function normalizePhoneNumber(phone: string | null | undefined): string {
  return (phone ?? ``).replace(/^\+1/, ``).replace(/\D/g, ``)
}

function createSendableNumberOption(
  phone: string | null | undefined,
  provider: NumberProvider,
  label?: string | null,
): SendableNumberOption | undefined {
  const normalized = normalizePhoneNumber(phone)
  if (!normalized) return undefined

  const formatted = formatPhoneNumber(normalized) ?? normalized

  return {
    id: normalized,
    // Provider is conveyed by the brand icon (meta.icon); alt text keeps it
    // available to screen readers.
    name: label ? `${formatted} (${label})` : formatted,
    value: normalized,
    meta: { provider, icon: PROVIDER_ICONS[provider], iconAlt: provider },
  }
}

/** Every number integrated on the location — the pool both primary-number pickers choose from. */
function getSendableNumberOptions(
  location: LocationType | undefined,
  smsNumbers: SmsNumberHit[],
): SendableNumberOption[] {
  const options: SendableNumberOption[] = []
  const seen = new Set<string>()

  const addUniqueOption = (option: SendableNumberOption | undefined): void => {
    if (!option || seen.has(option.id)) return
    seen.add(option.id)
    options.push(option)
  }

  for (const twilioNumber of location?.twilioNumbers ?? []) {
    if (twilioNumber?.type === `pool`) continue
    addUniqueOption(
      createSendableNumberOption(
        twilioNumber?.phone,
        `Twilio`,
        twilioNumber?.label || twilioNumber?.sourceName || twilioNumber?.type,
      ),
    )
  }

  for (const number of smsNumbers) {
    const provider: NumberProvider = number.provider === `twilio` ? `Twilio` : `CallRail`
    addUniqueOption(createSendableNumberOption(number.phone, provider, number.label))
  }

  return options
}

function extractLocation(result: { data?: unknown } | undefined): LocationType | undefined {
  const data = result?.data as {
    location?: LocationType
    hits?: Array<{ location?: LocationType }>
    // keep legacy fallback for older responses
    [key: string]: unknown
  }
  return data?.location ?? (data?.hits?.[0] as LocationType | undefined) ?? (data as LocationType | undefined)
}

function createNoneOption(): OptionType {
  return { id: `none`, name: `None`, value: `` }
}

/**
 * Matches a saved location number to its picker option. A saved number the
 * location no longer offers still renders as a plain option, so a stale value
 * stays visible instead of reading as "not set" and being silently cleared on
 * the next save.
 */
function resolveSelectedOption(saved: string | null | undefined, options: OptionType[]): OptionType | undefined {
  if (!saved) return undefined

  const normalized = normalizePhoneNumber(saved)
  return (
    options.find((option) => option.id === normalized) ?? {
      id: normalized,
      name: formatPhoneNumber(normalized) ?? normalized,
      value: normalized,
    }
  )
}

/** The "None" entry only earns a slot once there is a selection to clear. */
function withNoneOption(options: OptionType[], selected: OptionType | undefined): OptionType[] {
  return selected ? [createNoneOption(), ...options] : [...options]
}

export function useLocationPhoneNumbers(): UseLocationPhoneNumbersReturn {
  const { locationId } = useParams()
  const store = useLocationStore()

  const [location, setLocation] = useState<LocationType | undefined>()
  const [smsNumbers, setSmsNumbers] = useState<SmsNumberHit[]>([])
  const [numberOptions, setNumberOptions] = useState<OptionType[]>([])
  const [hasCallRailIntegration, setHasCallRailIntegration] = useState(false)
  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState<OptionType | undefined>(undefined)
  const [selectedSmsNumber, setSelectedSmsNumber] = useState<OptionType | undefined>(undefined)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (!locationId) {
      return
    }

    store
      .fetchOne(locationId, { requestPolicy: `network-only` })
      .then((result) => {
        const loc = extractLocation(result)
        if (!loc) return

        setLocation(loc)
        setHasCallRailIntegration(!!loc.callRailCompanyId)
      })
      .catch((err) => logError(`Error fetching location phone numbers`, err))
  }, [locationId])

  useEffect(() => {
    if (!locationId) return

    gqlQuery<{ locationSmsNumbers: SmsNumberHit[] }>(LOCATION_SMS_NUMBERS_QUERY, { locationId }).then((result) => {
      setSmsNumbers(result.data?.locationSmsNumbers ?? [])
    })
  }, [locationId])

  // Number options are synchronized from fetched location data and user selections.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  useEffect(() => {
    // Intentional synchronization: options are rebuilt from fetched data.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setNumberOptions(getSendableNumberOptions(location, smsNumbers))
  }, [location, smsNumbers])

  useEffect(() => {
    setSelectedTrackingNumber(resolveSelectedOption(location?.primaryTrackingNumber, numberOptions))
  }, [location, numberOptions])

  useEffect(() => {
    setSelectedSmsNumber(resolveSelectedOption(location?.primarySmsNumber, numberOptions))
  }, [location, numberOptions])

  const hasTwilioNumbers = !!(location?.twilioNumbers || []).some((n) => n.type !== `pool`)

  const defaultValues = location
    ? {
        officeMainNumber: formatPhoneNumber(location.officeMainNumber || location.businessPhone),
        officeTransferNumber: formatPhoneNumber(location.officeTransferNumber),
      }
    : undefined

  const hiddenInputs = [
    { name: `id`, value: location?.id },
    { name: `orgId`, value: location?.orgId },
    { name: `workspaceId`, value: location?.workspaceId },
  ]

  function handleInput(): void {
    setIsDirtyForm(true)
  }

  useEffect(() => {
    const formElement = formRef.current
    formElement?.addEventListener(`input`, handleInput)

    return () => {
      formElement?.removeEventListener(`input`, handleInput)
    }
  }, [formRef.current])

  function onSubmit(data?: Record<string, unknown>): void {
    if (!location?.id || !data) return

    const input: UpdateLocationInput = {}

    if (typeof data.officeMainNumber === `string`) input.officeMainNumber = data.officeMainNumber
    if (typeof data.officeTransferNumber === `string`) input.officeTransferNumber = data.officeTransferNumber
    input.primaryTrackingNumber = selectedTrackingNumber?.value || ``
    input.primarySmsNumber = selectedSmsNumber?.value || ``

    store.update(location.id, input).then((result) => {
      if (result.error) {
        notifyError(`Failed to save phone numbers`)
        return
      }

      notifySuccess(`Phone numbers saved`)
      setIsDirtyForm(false)
    })
  }

  function refetchLocation(): void {
    if (!locationId) return
    store.fetchOne(locationId, { requestPolicy: `network-only` }).then((result) => {
      const loc = extractLocation(result)
      if (loc) {
        setLocation(loc)
        setHasCallRailIntegration(!!loc.callRailCompanyId)
      }
    })
  }

  return {
    location,
    trackingNumberOptions: withNoneOption(numberOptions, selectedTrackingNumber),
    selectedTrackingNumber,
    setSelectedTrackingNumber,
    smsNumberOptions: withNoneOption(numberOptions, selectedSmsNumber),
    selectedSmsNumber,
    setSelectedSmsNumber,
    hasCallRailIntegration,
    hasTwilioNumbers,
    isDirtyForm,
    setIsDirtyForm,
    formRef,
    defaultValues,
    hiddenInputs,
    onSubmit,
    refetchLocation,
  }
}
